import { CATEGORIES } from '../constants/category';
import { CIDS } from '../../components/ProductsTable/constants';
import { getUniquePropertyValues } from '../helperFunctions';

import { IgbtAlgorithm } from './IgbtAlgorithm';
import { IpmAlgorithm } from './IpmAlgorithm';
import { BaseAlgorithm } from './BaseAlgorithm';
import { JFetAlgorithm } from './jFetAlgorithm';
import { SmartSwitchAlgorithm } from './SmartSwitchAlgorithm';
import { MosfetAlgorithm } from './MosfetAlgorithm';
import { OpAmplifierAlgorithm } from './OpAmplifierAlgorithm';
import { AnalogBJTAlgorithm } from './AnalogBJTAlgorithm';
import { DigitalBJTAlgorithm } from './DigitalBJTAlgorithm';
import { RfBJTAlgorithm } from './RfBJTAlgorithm';
import { ZenerDiodeAlogorithm } from './ZenerDiodeAlgorithm';
import { PowerDiodeAlgorithm } from './PowerDiodeAlgorithm';
import { SmallSignalAndRFDiodeAlgorithm } from './smallSignalAndRFDiodeAlgorithm';
import { GateDriverAlgorithm } from './GateDriverAlgorithm';
import { ComparatorAlgorithm } from './ComparatorAlgorithm';
import { PowerSupplyAlgorithm } from './PowerSupplyAlgorithm';
import { SmartPowerStageAlgorithm } from './smartPowerStageAlgorithm';
import { ProtectedPowerSwitchesAlgorithm } from './ProtectedPowerSwitchesAlgorithm';
import { ClockGenerationAlgorithm } from './ClockGenerationAlgorithm';
import { ImageSensorsAlgorithm } from './ImageSensorsAlgorithm';
import { PimAlgorithm } from './PimAlgorithm';
import { VoltageReferenceAlgorithm } from './VoltageReferenceAlgorithm';

export const RANKING_FILTER_CIDS = {
	[CATEGORIES.IGBT]: [CIDS.BREAKDOWN_VOLTAGE_CES, CIDS.COLLECTOR_CURRENT],
	[CATEGORIES.IPM]: [CIDS.RATED_VOLTAGE, CIDS.OUTPUT_CURRENT],
	[CATEGORIES.JFET]: [CIDS.BREAKDOWN_VOLTAGE_GSS, CIDS.DRAIN_CURRENT_MIN, CIDS.DRAIN_CURRENT_MAX],
	[CATEGORIES.SMART_SWITCH]: [CIDS.BREAKDOWN_VOLTAGE_DSS, CIDS.TYPICAL_CURRENT_LIMIT_FOR_25_DEGREES],
	[CATEGORIES.MOSFET]: [
		CIDS.BREAKDOWN_VOLTAGE_DSS,
		CIDS.GATE_SOURCE_VOLTAGE_MAX,
		CIDS.DRAIN_CURRENT_MAX,
		CIDS.ON_DS_RESISTANCE_TYPICAL_FOR_VGS_10V,
		CIDS.GATE_CHARGE_FOR_VGS_10
	],
	[CATEGORIES.OP_AMP]: [
		CIDS.GAIN_BANDWIDTH,
		CIDS.SLEW_RATE,
		CIDS.INPUT_OFFSET_VOLTAGE,
		CIDS.INPUT_REFERRED_VOLTAGE_NOISE_DENSITY,
		CIDS.QUIESCENT_CURRENT
	],
	[CATEGORIES.ANALOG_BJT]: [
		CIDS.CONTINUES_COLLECTOR_CURRENT,
		CIDS.COLLECTOR_EMITTER_VOLTAGE,
		CIDS.FORWARD_CURRENT_TRANSFER_RATIO_MIN
	],
	[CATEGORIES.DIGITAL_BJT]: [CIDS.V_I_ON_MIN, CIDS.V_I_OFF_MAX, CIDS.CONTINUES_COLLECTOR_CURRENT],
	[CATEGORIES.RF_BJT]: [
		CIDS.COLLECTOR_EMITTER_VOLTAGE,
		CIDS.TRANSITION_FREQUENCY,
		CIDS.CONTINUES_COLLECTOR_CURRENT,
		CIDS.FORWARD_CURRENT_TRANSFER_RATIO_MIN
	],
	[CATEGORIES.ZENER_DIODE]: [CIDS.TYPICAL_ZENER_VOLTAGE, CIDS.MAX_POWER],
	[CATEGORIES.POWER_DIODE]: [
		CIDS.REPETITIVE_REVERSE_VOLTAGE,
		CIDS.CONTINUES_FORWARD_CURRENT,
		CIDS.FORWARD_CURRENT_SURGE_PEAK
	],
	[CATEGORIES.SMALL_SIGNAL_DIODE]: [CIDS.REVERSE_VOLTAGE_MIN, CIDS.FORWARD_CURRENT_MAX, CIDS.REVERSE_CURRENT_MAX],
	[CATEGORIES.SMALL_SIGNAL_DIODE]: [CIDS.REVERSE_VOLTAGE_MIN, CIDS.FORWARD_CURRENT_MAX, CIDS.REVERSE_CURRENT_MAX],
	[CATEGORIES.GATE_DRIVER]: [CIDS.DRIVE_SINK_CURRENT_TYP, CIDS.V_IN_MAX, CIDS.V_CC_MAX],
	[CATEGORIES.COMPARATOR]: [CIDS.T_RES_TYP, CIDS.V_CC_MIN, CIDS.V_CC_MAX, CIDS.V_IO_MAX, CIDS.I_CC_TYP],
	[CATEGORIES.POWER_SUPPLY]: [CIDS.OUTPUT_CURRENT, CIDS.QUIESCENT_CURRENT],
	[CATEGORIES.SMART_POWER_STAGE]: [CIDS.V_IN_TYP, CIDS.I_OUT_MAX, CIDS.SWITCHING_FREQUENCY_MAX],
	[CATEGORIES.PROTECTED_POWER_SWITCH]: [CIDS.I_OUT_MAX, CIDS.V_IN_MIN, CIDS.V_IN_MAX],
	[CATEGORIES.CLOCK_GENERATION]: [CIDS.F_OUT_TYP, CIDS.F_IN_TYP, CIDS.V_CC_TYP],
	[CATEGORIES.IMAGE_SENSORS]: [CIDS.MEGAPIXELS, CIDS.PIXEL_SIZE, CIDS.FRAME_RATE, CIDS.OPTICAL_FORMAT_NUMBER],
	[CATEGORIES.POWER_INTEGRATED_MODULE]: [
		CIDS.BREAKDOWN_VOLTAGE_DSS,
		CIDS.RATED_CURRENT,
		CIDS.SATURATION_VOLTAGE,
		CIDS.ON_STATE_RESISTANCE_TYP,
		CIDS.V_F_TYP
	]
};

export const rankDebugLogEnable = false;
export const debugLog = [];
export class Ranking {
	constructor(products, category, filtersState, usedFilters, displayModeIsWPN) {
		this.products = products;
		this.category = category;
		this.displayModeIsWPN = displayModeIsWPN;

		switch (this.category) {
			case CATEGORIES.IGBT:
				this.algorithm = new IgbtAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.IPM:
				this.algorithm = new IpmAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.JFET:
				this.algorithm = new JFetAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.SMART_SWITCH:
				this.algorithm = new SmartSwitchAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.MOSFET:
				this.algorithm = new MosfetAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.OP_AMP:
				this.algorithm = new OpAmplifierAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.ANALOG_BJT:
				this.algorithm = new AnalogBJTAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.DIGITAL_BJT:
				this.algorithm = new DigitalBJTAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.RF_BJT:
				this.algorithm = new RfBJTAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.ZENER_DIODE:
				this.algorithm = new ZenerDiodeAlogorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.POWER_DIODE:
				this.algorithm = new PowerDiodeAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.SMALL_SIGNAL_DIODE:
				this.algorithm = new SmallSignalAndRFDiodeAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.GATE_DRIVER:
				this.algorithm = new GateDriverAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.COMPARATOR:
				this.algorithm = new ComparatorAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.POWER_SUPPLY:
				this.algorithm = new PowerSupplyAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.SMART_POWER_STAGE:
				this.algorithm = new SmartPowerStageAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.PROTECTED_POWER_SWITCH:
				this.algorithm = new ProtectedPowerSwitchesAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.CLOCK_GENERATION:
				this.algorithm = new ClockGenerationAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.IMAGE_SENSORS:
				this.algorithm = new ImageSensorsAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.POWER_INTEGRATED_MODULE:
				this.algorithm = new PimAlgorithm(usedFilters, filtersState);
				break;
			case CATEGORIES.VOLTAGE_REFERENCE:
				this.algorithm = new VoltageReferenceAlgorithm(usedFilters, filtersState);
				break;
			default:
				this.algorithm = new BaseAlgorithm(usedFilters, filtersState);
		}
	}

	/* 'rankWPN' value equals to the best (smallest) OPN rank of given WPN group */
	appendWPNRank(reRankedProducts) {
		let wpnRankedProducts = [];
		const uniqueWPNs = getUniquePropertyValues(reRankedProducts, CIDS.GENERAL_PART_NUMBER);
		let WPNGroupId = 1;

		uniqueWPNs.forEach(wpn => {
			const groupOfWPNs = reRankedProducts.filter(product => product.gpn === wpn);
			const bestRank = groupOfWPNs.reduce((prev, current) => (prev.rank < current.rank ? prev : current)).rank;

			groupOfWPNs.map((product, index) =>
				wpnRankedProducts.push({
					...product,
					[CIDS.RANK_WPN]: bestRank,
					WPNGroup: {
						id: WPNGroupId,
						productId: index + 1,
						productsTotal: groupOfWPNs.length
					}
				})
			);
			WPNGroupId++;
		});
		return wpnRankedProducts;
	}

	update(similarParts) {
		this.algorithm.logHeadingsToConsole();

		let reRankedProducts = this.products.map(product => {
			const newRank = this.algorithm.execute(product);

			if (rankDebugLogEnable) {
				console.log(debugLog.join());
			}

			return {
				...product,
				[CIDS.RANK]: newRank
			};
		});

		const ranks = [];
		reRankedProducts.forEach(function (reRankedProduct) {
			ranks.push(reRankedProduct.rank);
		});

		let rankMinMax = [Math.min(...ranks), Math.max(...ranks)];

		if (similarParts) {
			reRankedProducts = this.modifyRankForSimilarProduct(reRankedProducts, similarParts, rankMinMax);
		}

		const reRankedProductsWithPartSuitability = reRankedProducts.map(reRankedProduct => {
			const partSuitability = Math.round((1 - (reRankedProduct.rank - rankMinMax[0])) * 100);

			return {
				...reRankedProduct,
				[CIDS.PART_SUITABILITY]: partSuitability
			};
		});

		const sortedRankedProductsByRank = reRankedProductsWithPartSuitability.sort((a, b) => {
			return a.rank - b.rank;
		});
		sortedRankedProductsByRank.forEach((item, index) => {
			item.partRankIndex = index + 1;
		});

		return this.displayModeIsWPN ? this.appendWPNRank(sortedRankedProductsByRank) : sortedRankedProductsByRank;
	}

	modifyRankForSimilarProduct(reRankedProducts, similarParts, rankMinMax) {
		const rankShiftLevel = 0.0001;
		reRankedProducts = reRankedProducts.map(product => {
			let newRank;

			if (similarParts.includes(product[CIDS.PART_NUMBER])) {
				newRank =
					rankMinMax[0] - (similarParts.length - similarParts.indexOf(product[CIDS.PART_NUMBER])) * rankShiftLevel;
			} else {
				newRank = product[CIDS.RANK];
			}

			return {
				...product,
				[CIDS.RANK]: newRank
			};
		});
		rankMinMax[0] = rankMinMax[0] - (similarParts.length - 1) * rankShiftLevel;
		return reRankedProducts;
	}
}
